<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { VForm } from 'vuetify/components'
import type { OptionsType } from '@haskoning/vue-basestore'
import type { Project, ProjectFieldErrors } from '@/stores/type/project.type'

type FieldType = OptionsType['actions']['POST'][string]

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    item: Project
    fields: FieldType[]
    workOrderField?: FieldType
    disabledFields?: string[]
    loading?: boolean
    errors?: ProjectFieldErrors
  }>(),
  {
    disabledFields: () => [],
    loading: false,
    errors: () => ({})
  }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  submit: [value: Project]
  clearError: [field: string]
}>()

const formRef = ref<VForm>()
const isValid = ref(false)
const draft = ref<Record<string, unknown>>({})

const dialog = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value)
})

const fallbackWorkOrderField: FieldType = {
  type: 'string',
  required: true,
  read_only: false,
  label: 'Work Order',
  max_length: 20,
  field_type: 'string',
  parameter: 'work_order'
}

const workOrderField = computed(() => props.workOrderField ?? fallbackWorkOrderField)
const workOrderLabel = computed(() =>
  workOrderField.value.parameter === 'work_order' ? 'Work Order' : workOrderField.value.label
)
const formFields = computed(() =>
  props.fields.filter((field) => field.parameter !== workOrderField.value.parameter)
)
const displayedFieldNames = computed(
  () =>
    new Set([workOrderField.value.parameter, ...formFields.value.map((field) => field.parameter)])
)
const generalErrors = computed(() =>
  Object.entries(props.errors)
    .filter(([field]) => !displayedFieldNames.value.has(field))
    .flatMap(([, messages]) => messages)
)
const isEdit = computed(() => Boolean(props.item?.id))

const isNumericField = (field: FieldType) =>
  ['decimal', 'float', 'integer', 'number'].includes(field.type) ||
  ['decimalfield', 'floatfield', 'integerfield', 'numberfield'].includes(
    field.field_type?.toLowerCase()
  )

const fieldRules = (field: FieldType) => [
  (value: unknown) =>
    !field.required ||
    (value !== null && value !== undefined && String(value).trim() !== '') ||
    'Required.',
  (value: unknown) =>
    !field.max_length ||
    value === null ||
    value === undefined ||
    String(value).length <= field.max_length ||
    `Use at most ${field.max_length} characters.`,
  (value: unknown) =>
    field.min_value === undefined ||
    value === null ||
    value === '' ||
    Number(value) >= field.min_value ||
    `Use a value of at least ${field.min_value}.`,
  (value: unknown) =>
    field.max_value === undefined ||
    value === null ||
    value === '' ||
    Number(value) <= field.max_value ||
    `Use a value of at most ${field.max_value}.`
]

const workOrderRules = computed(() => [
  ...fieldRules(workOrderField.value),
  (value: unknown) =>
    !value || /^[A-Z]{2}\d{4}$/.test(String(value).trim()) || 'Work Order must be in AA1111 format.'
])

const resetForm = () => {
  draft.value = { ...(props.item ?? {}) }
  for (const field of [workOrderField.value, ...formFields.value]) {
    if (draft.value[field.parameter] === undefined && field.default !== undefined) {
      draft.value[field.parameter] = field.default
    }
  }
  void nextTick(() => formRef.value?.resetValidation())
}

const updateField = (field: string, value: unknown) => {
  draft.value = { ...draft.value, [field]: value }
  emit('clearError', field)
}

const close = () => {
  if (props.loading) return
  dialog.value = false
}

const submit = async () => {
  const validationResult = await formRef.value?.validate()
  if (!validationResult?.valid) return

  const payload: Record<string, unknown> = {}
  if (props.item?.id) payload.id = props.item.id
  for (const field of [workOrderField.value, ...formFields.value]) {
    payload[field.parameter] = draft.value[field.parameter]
  }
  emit('submit', payload as Project)
}

watch(
  () => [props.modelValue, props.item] as const,
  ([isOpen]) => {
    if (isOpen) resetForm()
  },
  { immediate: true }
)
</script>

<template>
  <v-dialog v-model="dialog" max-width="400" :persistent="loading" @click:outside="close">
    <v-card class="form-dialog pa-3">
      <v-card-title>{{ isEdit ? 'Update' : 'Create' }} project</v-card-title>

      <v-card-text class="pl-4">
        <v-alert
          v-if="generalErrors.length"
          class="mb-4"
          density="compact"
          type="error"
          variant="tonal"
        >
          <div v-for="message in generalErrors" :key="message">{{ message }}</div>
        </v-alert>

        <v-form ref="formRef" v-model="isValid" @submit.prevent="submit">
          <v-text-field
            :model-value="draft[workOrderField.parameter]"
            color="primary"
            :disabled="disabledFields.includes(workOrderField.parameter)"
            :error-messages="errors[workOrderField.parameter] ?? []"
            :label="workOrderLabel"
            :rules="workOrderRules"
            variant="filled"
            @update:model-value="updateField(workOrderField.parameter, $event)"
          />

          <template v-for="field in formFields" :key="field.parameter">
            <v-select
              v-if="field.type === 'choice'"
              :model-value="draft[field.parameter]"
              color="primary"
              :disabled="disabledFields.includes(field.parameter)"
              :error-messages="errors[field.parameter] ?? []"
              item-title="display_name"
              item-value="value"
              :items="field.choices ?? []"
              :label="field.label"
              :menu-props="{ zIndex: 2500 }"
              :rules="fieldRules(field)"
              variant="filled"
              @update:model-value="updateField(field.parameter, $event)"
            />
            <v-textarea
              v-else-if="field.parameter === 'description'"
              :model-value="draft[field.parameter]"
              color="primary"
              :counter="field.max_length || undefined"
              :disabled="disabledFields.includes(field.parameter)"
              :error-messages="errors[field.parameter] ?? []"
              :label="field.label"
              :rules="fieldRules(field)"
              rows="5"
              variant="filled"
              @update:model-value="updateField(field.parameter, $event)"
            />
            <v-text-field
              v-else
              :model-value="draft[field.parameter]"
              color="primary"
              :counter="!isNumericField(field) && field.max_length ? field.max_length : undefined"
              :disabled="disabledFields.includes(field.parameter)"
              :error-messages="errors[field.parameter] ?? []"
              :label="field.label"
              :min="field.min_value"
              :max="field.max_value"
              :rules="fieldRules(field)"
              :step="isNumericField(field) ? '0.01' : undefined"
              :type="isNumericField(field) ? 'number' : 'text'"
              variant="filled"
              @update:model-value="updateField(field.parameter, $event)"
            />
          </template>
        </v-form>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn class="text-none" color="primary" :disabled="loading" @click="close"> Cancel </v-btn>
        <v-btn
          class="text-none"
          color="primary"
          :disabled="!isValid || loading"
          :loading="loading"
          @click="submit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
